import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { 
  FiSave, 
  FiRefreshCw,
  FiHome,
  FiInfo,
  FiGrid,
  FiBarChart2,
  FiPhone,
  FiSettings,
  FiPlus,
  FiTrash2,
  FiEdit2,
  FiChevronUp,
  FiChevronDown,
  FiAward,
  FiMessageSquare,
  FiZap,
  FiImage
} from 'react-icons/fi';
import { useContent } from '../../context/ContentContext';
import ImagePickerModal from '../../components/ImagePickerModal';
import { getImageUrl } from '../../services/api';
import './AdminContent.css';

const AdminContent = () => {
  const { content, updateContent, refreshContent } = useContent();
  const [activeSection, setActiveSection] = useState('hero');
  const [formData, setFormData] = useState(content);
  const [saving, setSaving] = useState(false);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [imagePickerTarget, setImagePickerTarget] = useState(null); // { section: 'about', field: 'image' }

  // Resim seçici açma fonksiyonu
  const openImagePicker = (section, field) => {
    setImagePickerTarget({ section, field });
    setImagePickerOpen(true);
  };

  // Resim seçildiğinde
  const handleImageSelect = (imageUrl) => {
    if (imagePickerTarget) {
      handleChange(imagePickerTarget.section, imagePickerTarget.field, imageUrl);
    }
    setImagePickerOpen(false);
    setImagePickerTarget(null);
  };

  // Content değiştiğinde formData'yı güncelle
  useEffect(() => {
    setFormData(content);
  }, [content]);

  const sections = [
    { id: 'hero', label: 'Ana Sayfa Hero', icon: FiHome },
    { id: 'features', label: 'Özellikler', icon: FiAward },
    { id: 'about', label: 'Hakkımızda', icon: FiInfo },
    { id: 'services', label: 'Programlar', icon: FiGrid },
    { id: 'scholarship', label: 'Bursluluk', icon: FiAward },
    { id: 'gallery', label: 'Galeri Kategorileri', icon: FiImage },
    { id: 'testimonials', label: 'Yorumlar', icon: FiMessageSquare },
    { id: 'stats', label: 'İstatistikler', icon: FiBarChart2 },
    { id: 'cta', label: 'CTA Banner', icon: FiZap },
    { id: 'contact', label: 'İletişim', icon: FiPhone },
    { id: 'general', label: 'Genel Ayarlar', icon: FiSettings }
  ];

  const handleChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await updateContent(activeSection, formData[activeSection]);
      if (result.success) {
        toast.success('İçerik güncellendi');
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('Güncelleme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setFormData(content);
    toast.success('Değişiklikler geri alındı');
  };

  const renderHeroForm = () => (
    <div className="content-form">
      <div className="form-group">
        <label className="form-label">Rozet Metni</label>
        <input
          type="text"
          className="form-input"
          placeholder="15+ Yıllık Eğitim Tecrübesi"
          value={formData.hero?.badge || ''}
          onChange={(e) => handleChange('hero', 'badge', e.target.value)}
        />
      </div>
      <div className="form-group">
        <label className="form-label">Başlık</label>
        <input
          type="text"
          className="form-input"
          value={formData.hero?.title || ''}
          onChange={(e) => handleChange('hero', 'title', e.target.value)}
        />
      </div>
      <div className="form-group">
        <label className="form-label">Alt Başlık</label>
        <input
          type="text"
          className="form-input"
          value={formData.hero?.subtitle || ''}
          onChange={(e) => handleChange('hero', 'subtitle', e.target.value)}
        />
      </div>
      <div className="form-group">
        <label className="form-label">Açıklama</label>
        <textarea
          className="form-textarea"
          rows={3}
          value={formData.hero?.description || ''}
          onChange={(e) => handleChange('hero', 'description', e.target.value)}
        />
      </div>
      
      <h4 className="form-subtitle">Birincil Buton</h4>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Buton Metni</label>
          <input
            type="text"
            className="form-input"
            value={formData.hero?.buttonText || 'Bursluluk Sınavına Başvur'}
            onChange={(e) => handleChange('hero', 'buttonText', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Buton Linki</label>
          <input
            type="text"
            className="form-input"
            placeholder="/bursluluk"
            value={formData.hero?.buttonLink || '/bursluluk'}
            onChange={(e) => handleChange('hero', 'buttonLink', e.target.value)}
          />
        </div>
      </div>
      
      <h4 className="form-subtitle">İkincil Buton</h4>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Buton Metni</label>
          <input
            type="text"
            className="form-input"
            value={formData.hero?.secondaryButtonText || 'Bizi Tanıyın'}
            onChange={(e) => handleChange('hero', 'secondaryButtonText', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Buton Linki</label>
          <input
            type="text"
            className="form-input"
            placeholder="/hakkimizda"
            value={formData.hero?.secondaryButtonLink || '/hakkimizda'}
            onChange={(e) => handleChange('hero', 'secondaryButtonLink', e.target.value)}
          />
        </div>
      </div>

      <h4 className="form-subtitle">Bilgi Kartları</h4>
      <p className="form-info">Çemberin yanında gözüken beyaz kutulardaki yazılar</p>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Kart 1 Metni</label>
          <input
            type="text"
            className="form-input"
            placeholder="%95 Başarı"
            value={formData.hero?.card1Text || '%95 Başarı'}
            onChange={(e) => handleChange('hero', 'card1Text', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Kart 2 Metni</label>
          <input
            type="text"
            className="form-input"
            placeholder="5000+ Mezun"
            value={formData.hero?.card2Text || '5000+ Mezun'}
            onChange={(e) => handleChange('hero', 'card2Text', e.target.value)}
          />
        </div>
      </div>
    </div>
  );

  const defaultWhyUs = [
    'Deneyimli ve uzman öğretmen kadrosu',
    'Bireysel takip ve rehberlik hizmeti',
    'Modern ve teknolojik sınıflar',
    'Düzenli deneme sınavları ve analiz',
    'Veli bilgilendirme toplantıları'
  ];

  const defaultValues = [
    { icon: 'heart', title: 'Öğrenci Odaklılık', desc: 'Her öğrencinin benzersiz olduğuna inanır, bireysel gelişimi destekleriz.' },
    { icon: 'target', title: 'Başarı Odaklılık', desc: 'Hedeflerimiz büyük, azimle çalışır ve başarıya ulaşırız.' },
    { icon: 'users', title: 'Takım Ruhu', desc: 'Öğretmenler, öğrenciler ve veliler olarak birlikte çalışırız.' },
    { icon: 'book', title: 'Sürekli Gelişim', desc: 'Eğitim metodlarımızı sürekli güncel tutarız.' }
  ];

  const defaultMilestones = [
    { year: '2009', title: 'Kuruluş', desc: 'BMK Eğitim Kurumları kuruldu' },
    { year: '2012', title: 'Büyüme', desc: 'İkinci şubemizi açtık' },
    { year: '2015', title: 'Genişleme', desc: 'Online eğitim platformu başladı' },
    { year: '2018', title: 'Yenilik', desc: 'Modern teknoloji sınıfları' },
    { year: '2024', title: 'Bugün', desc: '5000+ mezun, %95 başarı' }
  ];

  const valueIconOptions = [
    { value: 'heart', label: '❤️ Kalp' },
    { value: 'target', label: '🎯 Hedef' },
    { value: 'users', label: '👥 Kullanıcılar' },
    { value: 'book', label: '📚 Kitap' },
    { value: 'award', label: '🏆 Ödül' },
    { value: 'check', label: '✓ Onay' }
  ];

  const addWhyUsItem = () => {
    const current = formData.about?.whyUs || defaultWhyUs;
    setFormData(prev => ({
      ...prev,
      about: { ...prev.about, whyUs: [...current, { title: 'Yeni Başlık', desc: 'Açıklama' }] }
    }));
  };

  const updateWhyUsItem = (index, field, value) => {
    const current = formData.about?.whyUs || defaultWhyUs;
    const newItems = [...current];
    // String formatından object formatına dönüştür (eski veri uyumluluğu)
    if (typeof newItems[index] === 'string') {
      newItems[index] = { title: newItems[index], desc: '' };
    }
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData(prev => ({
      ...prev,
      about: { ...prev.about, whyUs: newItems }
    }));
  };

  const deleteWhyUsItem = (index) => {
    if (!window.confirm('Bu maddeyi silmek istediğinize emin misiniz?')) return;
    const current = formData.about?.whyUs || defaultWhyUs;
    setFormData(prev => ({
      ...prev,
      about: { ...prev.about, whyUs: current.filter((_, i) => i !== index) }
    }));
  };

  // Values (Değerlerimiz) functions
  const addValue = () => {
    const current = formData.about?.values || defaultValues;
    setFormData(prev => ({
      ...prev,
      about: { ...prev.about, values: [...current, { icon: 'heart', title: 'Yeni Değer', desc: 'Açıklama' }] }
    }));
  };

  const updateValue = (index, field, value) => {
    const current = formData.about?.values || defaultValues;
    const newItems = [...current];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData(prev => ({
      ...prev,
      about: { ...prev.about, values: newItems }
    }));
  };

  const deleteValue = (index) => {
    if (!window.confirm('Bu değeri silmek istediğinize emin misiniz?')) return;
    const current = formData.about?.values || defaultValues;
    setFormData(prev => ({
      ...prev,
      about: { ...prev.about, values: current.filter((_, i) => i !== index) }
    }));
  };

  // Milestones (Yolculuğumuz) functions
  const addMilestone = () => {
    const current = formData.about?.milestones || defaultMilestones;
    setFormData(prev => ({
      ...prev,
      about: { ...prev.about, milestones: [...current, { year: '2024', title: 'Yeni', desc: 'Açıklama' }] }
    }));
  };

  const updateMilestone = (index, field, value) => {
    const current = formData.about?.milestones || defaultMilestones;
    const newItems = [...current];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData(prev => ({
      ...prev,
      about: { ...prev.about, milestones: newItems }
    }));
  };

  const deleteMilestone = (index) => {
    if (!window.confirm('Bu kilometre taşını silmek istediğinize emin misiniz?')) return;
    const current = formData.about?.milestones || defaultMilestones;
    setFormData(prev => ({
      ...prev,
      about: { ...prev.about, milestones: current.filter((_, i) => i !== index) }
    }));
  };

  const renderAboutForm = () => {
    const whyUsList = formData.about?.whyUs || defaultWhyUs;
    const valuesList = formData.about?.values || defaultValues;
    const milestonesList = formData.about?.milestones || defaultMilestones;
    
    return (
      <div className="content-form">
        <h4 className="form-subtitle">Sayfa Hero Bölümü</h4>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Hero Başlık</label>
            <input
              type="text"
              className="form-input"
              value={formData.about?.heroTitle || 'Hakkımızda'}
              onChange={(e) => handleChange('about', 'heroTitle', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Hero Alt Başlık</label>
            <input
              type="text"
              className="form-input"
              value={formData.about?.heroSubtitle || '15 yılı aşkın tecrübemizle geleceğin liderlerini yetiştiriyoruz'}
              onChange={(e) => handleChange('about', 'heroSubtitle', e.target.value)}
            />
          </div>
        </div>

        <h4 className="form-subtitle">Hikayemiz Bölümü</h4>
        <div className="form-group">
          <label className="form-label">Başlık</label>
          <input
            type="text"
            className="form-input"
            value={formData.about?.title || 'Hikayemiz'}
            onChange={(e) => handleChange('about', 'title', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Açıklama</label>
          <textarea
            className="form-textarea"
            rows={5}
            value={formData.about?.description || ''}
            onChange={(e) => handleChange('about', 'description', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Hakkımızda Resmi</label>
          <div className="image-picker-field">
            {formData.about?.image ? (
              <div className="image-preview">
                <img src={getImageUrl(formData.about.image)} alt="Hakkımızda" />
              </div>
            ) : (
              <div className="image-placeholder-box">
                <FiImage />
                <span>Resim seçilmedi</span>
              </div>
            )}
            <button 
              type="button" 
              className="btn btn-sm btn-primary"
              onClick={() => openImagePicker('about', 'image')}
            >
              <FiImage /> Resim Seç
            </button>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Neden Biz Resmi</label>
          <div className="image-picker-field">
            {formData.about?.whyUsImage ? (
              <div className="image-preview">
                <img src={getImageUrl(formData.about.whyUsImage)} alt="Neden Biz" />
              </div>
            ) : (
              <div className="image-placeholder-box">
                <FiImage />
                <span>Resim seçilmedi</span>
              </div>
            )}
            <button 
              type="button" 
              className="btn btn-sm btn-primary"
              onClick={() => openImagePicker('about', 'whyUsImage')}
            >
              <FiImage /> Resim Seç
            </button>
          </div>
        </div>
        
        <div className="programs-header">
          <h3 className="form-subtitle" style={{ margin: 0, padding: 0, border: 'none' }}>Neden Biz? Listesi</h3>
          <button type="button" className="btn btn-sm btn-primary" onClick={addWhyUsItem}>
            <FiPlus /> Ekle
          </button>
        </div>
        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <label className="form-label">Bölüm Başlığı</label>
          <input
            type="text"
            className="form-input"
            value={formData.about?.whyUsTitle || 'Neden Biz?'}
            onChange={(e) => handleChange('about', 'whyUsTitle', e.target.value)}
          />
        </div>
        
        <div className="programs-list">
          {whyUsList.map((item, index) => {
            // String formatını object formatına dönüştür (eski veri uyumluluğu)
            const itemData = typeof item === 'string' ? { title: item, desc: '' } : item;
            return (
              <div key={index} className="program-item">
                <div className="program-item-header">
                  <span className="program-item-title">{itemData.title}</span>
                  <button 
                    type="button"
                    className="btn-icon btn-danger"
                    onClick={() => deleteWhyUsItem(index)}
                  >
                    <FiTrash2 />
                  </button>
                </div>
                <div className="program-item-body">
                  <div className="form-group">
                    <label className="form-label">Başlık</label>
                    <input
                      type="text"
                      className="form-input"
                      value={itemData.title}
                      onChange={(e) => updateWhyUsItem(index, 'title', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Açıklama</label>
                    <input
                      type="text"
                      className="form-input"
                      value={itemData.desc || ''}
                      onChange={(e) => updateWhyUsItem(index, 'desc', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Değerlerimiz Bölümü */}
        <div className="programs-header" style={{ marginTop: '2rem' }}>
          <h3 className="form-subtitle" style={{ margin: 0, padding: 0, border: 'none' }}>Değerlerimiz (/hakkimizda sayfası)</h3>
          <button type="button" className="btn btn-sm btn-primary" onClick={addValue}>
            <FiPlus /> Ekle
          </button>
        </div>
        <div className="form-row" style={{ marginBottom: '1rem' }}>
          <div className="form-group">
            <label className="form-label">Bölüm Başlığı</label>
            <input
              type="text"
              className="form-input"
              value={formData.about?.valuesTitle || 'Değerlerimiz'}
              onChange={(e) => handleChange('about', 'valuesTitle', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Bölüm Alt Başlığı</label>
            <input
              type="text"
              className="form-input"
              value={formData.about?.valuesSubtitle || 'Eğitim anlayışımızın temelini oluşturan değerler'}
              onChange={(e) => handleChange('about', 'valuesSubtitle', e.target.value)}
            />
          </div>
        </div>
        <div className="programs-list">
          {valuesList.map((item, index) => (
            <div key={index} className="program-item">
              <div className="program-item-header">
                <span className="program-item-title">{item.title}</span>
                <button 
                  type="button"
                  className="btn-icon btn-danger"
                  onClick={() => deleteValue(index)}
                >
                  <FiTrash2 />
                </button>
              </div>
              <div className="program-item-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Başlık</label>
                    <input
                      type="text"
                      className="form-input"
                      value={item.title}
                      onChange={(e) => updateValue(index, 'title', e.target.value)}
                    />
                  </div>
                  <div className="form-group" style={{ maxWidth: '150px' }}>
                    <label className="form-label">İkon</label>
                    <select
                      className="form-select"
                      value={item.icon}
                      onChange={(e) => updateValue(index, 'icon', e.target.value)}
                    >
                      {valueIconOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Açıklama</label>
                  <input
                    type="text"
                    className="form-input"
                    value={item.desc}
                    onChange={(e) => updateValue(index, 'desc', e.target.value)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Yolculuğumuz Bölümü */}
        <div className="programs-header" style={{ marginTop: '2rem' }}>
          <h3 className="form-subtitle" style={{ margin: 0, padding: 0, border: 'none' }}>Yolculuğumuz (/hakkimizda sayfası)</h3>
          <button type="button" className="btn btn-sm btn-primary" onClick={addMilestone}>
            <FiPlus /> Ekle
          </button>
        </div>
        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <label className="form-label">Bölüm Başlığı</label>
          <input
            type="text"
            className="form-input"
            value={formData.about?.milestonesTitle || 'Yolculuğumuz'}
            onChange={(e) => handleChange('about', 'milestonesTitle', e.target.value)}
          />
        </div>
        <div className="programs-list">
          {milestonesList.map((item, index) => (
            <div key={index} className="program-item">
              <div className="program-item-header">
                <span className="program-item-title">{item.year} - {item.title}</span>
                <button 
                  type="button"
                  className="btn-icon btn-danger"
                  onClick={() => deleteMilestone(index)}
                >
                  <FiTrash2 />
                </button>
              </div>
              <div className="program-item-body">
                <div className="form-row">
                  <div className="form-group" style={{ maxWidth: '100px' }}>
                    <label className="form-label">Yıl</label>
                    <input
                      type="text"
                      className="form-input"
                      value={item.year}
                      onChange={(e) => updateMilestone(index, 'year', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Başlık</label>
                    <input
                      type="text"
                      className="form-input"
                      value={item.title}
                      onChange={(e) => updateMilestone(index, 'title', e.target.value)}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Açıklama</label>
                  <input
                    type="text"
                    className="form-input"
                    value={item.desc}
                    onChange={(e) => updateMilestone(index, 'desc', e.target.value)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderStatsForm = () => (
    <div className="content-form">
      <p className="form-info">İstatistikleri düzenleyin (örn: 5000+, 50+, 15+, %95)</p>
      {formData.stats?.items?.map((item, index) => (
        <div key={index} className="form-row">
          <div className="form-group">
            <label className="form-label">Sayı {index + 1}</label>
            <input
              type="text"
              className="form-input"
              value={item.number}
              onChange={(e) => {
                const newItems = [...formData.stats.items];
                newItems[index] = { ...newItems[index], number: e.target.value };
                setFormData(prev => ({
                  ...prev,
                  stats: { ...prev.stats, items: newItems }
                }));
              }}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Etiket {index + 1}</label>
            <input
              type="text"
              className="form-input"
              value={item.label}
              onChange={(e) => {
                const newItems = [...formData.stats.items];
                newItems[index] = { ...newItems[index], label: e.target.value };
                setFormData(prev => ({
                  ...prev,
                  stats: { ...prev.stats, items: newItems }
                }));
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );

  const renderContactForm = () => (
    <div className="content-form">
      <div className="form-group">
        <label className="form-label">Adres</label>
        <input
          type="text"
          className="form-input"
          value={formData.contact?.address || ''}
          onChange={(e) => handleChange('contact', 'address', e.target.value)}
        />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Telefon (Arama)</label>
          <input
            type="text"
            className="form-input"
            placeholder="0212 123 45 67"
            value={formData.contact?.phone || ''}
            onChange={(e) => handleChange('contact', 'phone', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">WhatsApp Numarası</label>
          <input
            type="text"
            className="form-input"
            placeholder="905551234567"
            value={formData.contact?.whatsappPhone || ''}
            onChange={(e) => handleChange('contact', 'whatsappPhone', e.target.value)}
          />
          <small style={{ color: 'var(--gray-500)', fontSize: '0.75rem' }}>Başında 90 ile, boşluksuz</small>
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">E-posta</label>
        <input
          type="email"
          className="form-input"
          value={formData.contact?.email || ''}
          onChange={(e) => handleChange('contact', 'email', e.target.value)}
        />
      </div>
      <div className="form-group">
        <label className="form-label">WhatsApp Otomatik Mesaj</label>
        <textarea
          className="form-textarea"
          rows={2}
          placeholder="Merhaba, bilgi almak istiyorum."
          value={formData.contact?.whatsappMessage || ''}
          onChange={(e) => handleChange('contact', 'whatsappMessage', e.target.value)}
        />
        <small style={{ color: 'var(--gray-500)', fontSize: '0.75rem' }}>Kullanıcı WhatsApp'a tıkladığında otomatik yazılacak mesaj</small>
      </div>
      <div className="form-group">
        <label className="form-label">Çalışma Saatleri</label>
        <input
          type="text"
          className="form-input"
          value={formData.contact?.workingHours || ''}
          onChange={(e) => handleChange('contact', 'workingHours', e.target.value)}
        />
      </div>
      <h4 className="form-subtitle">Sosyal Medya</h4>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Facebook</label>
          <input
            type="url"
            className="form-input"
            placeholder="https://facebook.com/..."
            value={formData.contact?.socialMedia?.facebook || ''}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              contact: {
                ...prev.contact,
                socialMedia: { ...prev.contact?.socialMedia, facebook: e.target.value }
              }
            }))}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Instagram</label>
          <input
            type="url"
            className="form-input"
            placeholder="https://instagram.com/..."
            value={formData.contact?.socialMedia?.instagram || ''}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              contact: {
                ...prev.contact,
                socialMedia: { ...prev.contact?.socialMedia, instagram: e.target.value }
              }
            }))}
          />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">X (Twitter)</label>
        <input
          type="url"
          className="form-input"
          placeholder="https://x.com/..."
          value={formData.contact?.socialMedia?.twitter || ''}
          onChange={(e) => setFormData(prev => ({
            ...prev,
            contact: {
              ...prev.contact,
              socialMedia: { ...prev.contact?.socialMedia, twitter: e.target.value }
            }
          }))}
        />
      </div>
    </div>
  );

  const renderGeneralForm = () => (
    <div className="content-form">
      <div className="form-group">
        <label className="form-label">Site Adı</label>
        <input
          type="text"
          className="form-input"
          value={formData.general?.siteName || ''}
          onChange={(e) => handleChange('general', 'siteName', e.target.value)}
        />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Ana Renk</label>
          <div className="color-input-wrapper">
            <input
              type="color"
              className="color-input"
              value={formData.general?.primaryColor || '#1e3a5f'}
              onChange={(e) => handleChange('general', 'primaryColor', e.target.value)}
            />
            <input
              type="text"
              className="form-input"
              value={formData.general?.primaryColor || '#1e3a5f'}
              onChange={(e) => handleChange('general', 'primaryColor', e.target.value)}
            />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">İkincil Renk</label>
          <div className="color-input-wrapper">
            <input
              type="color"
              className="color-input"
              value={formData.general?.secondaryColor || '#f59e0b'}
              onChange={(e) => handleChange('general', 'secondaryColor', e.target.value)}
            />
            <input
              type="text"
              className="form-input"
              value={formData.general?.secondaryColor || '#f59e0b'}
              onChange={(e) => handleChange('general', 'secondaryColor', e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const defaultPrograms = [
    { 
      id: 1,
      title: 'LGS Hazırlık', 
      description: '8. sınıf öğrencileri için kapsamlı LGS hazırlık programı. Türkçe, Matematik, Fen Bilimleri, Sosyal Bilgiler ve İngilizce derslerinde tam donanımlı eğitim.',
      features: ['Haftalık deneme sınavları', 'Birebir rehberlik', 'Konu takip sistemi', 'Online destek'],
      color: '#667eea'
    },
    { 
      id: 2,
      title: 'YKS-TYT Hazırlık', 
      description: 'Temel Yeterlilik Testi için kapsamlı hazırlık programı. Tüm derslerde uzman kadro eşliğinde eğitim.',
      features: ['Günlük etüt', 'Haftalık denemeler', 'Motivasyon seminerleri', 'Veli görüşmeleri'],
      color: '#f093fb'
    },
    { 
      id: 3,
      title: 'YKS-AYT Hazırlık', 
      description: 'Alan Yeterlilik Testi için Sayısal, Eşit Ağırlık ve Sözel alanlarda özel hazırlık programları.',
      features: ['Alan bazlı eğitim', 'Soru çözüm atölyeleri', 'Kamp programları', 'Tercih danışmanlığı'],
      color: '#4facfe'
    },
    { 
      id: 4,
      title: 'Yabancı Dil', 
      description: 'İngilizce ve Almanca dil kursları. Cambridge sertifika programları ve YDS hazırlık.',
      features: ['Native speaker eğitmenler', 'Konuşma kulüpleri', 'Sertifika programları', 'Online pratik'],
      color: '#43e97b'
    }
  ];

  // Helper: programs veya items'dan al (eski veri uyumluluğu)
  const getPrograms = () => {
    const programs = formData.services?.programs || formData.services?.items;
    if (programs && programs.length > 0) {
      // Eski formattaki verileri yeni formata dönüştür
      return programs.map((p, index) => ({
        id: p.id || index + 1,
        title: p.title || '',
        subtitle: p.subtitle || '',
        description: p.description || '',
        features: p.features || [],
        duration: p.duration || '',
        classSize: p.classSize || '',
        color: p.color || '#667eea'
      }));
    }
    return defaultPrograms;
  };

  const addProgram = () => {
    const currentPrograms = getPrograms();
    const newProgram = {
      id: Date.now(),
      title: 'Yeni Program',
      subtitle: 'Alt başlık',
      description: 'Program açıklaması',
      features: ['Özellik 1', 'Özellik 2'],
      duration: '9 Ay',
      classSize: 'Max 15 Kişi',
      color: '#667eea'
    };
    setFormData(prev => ({
      ...prev,
      services: {
        ...prev.services,
        programs: [...currentPrograms, newProgram]
      }
    }));
  };

  const updateProgram = (index, field, value) => {
    const currentPrograms = getPrograms();
    const newPrograms = [...currentPrograms];
    newPrograms[index] = { ...newPrograms[index], [field]: value };
    setFormData(prev => ({
      ...prev,
      services: {
        ...prev.services,
        programs: newPrograms
      }
    }));
  };

  const deleteProgram = (index) => {
    if (!window.confirm('Bu programı silmek istediğinize emin misiniz?')) return;
    const currentPrograms = getPrograms();
    const newPrograms = currentPrograms.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      services: {
        ...prev.services,
        programs: newPrograms
      }
    }));
  };

  const moveProgram = (index, direction) => {
    const currentPrograms = getPrograms();
    const newPrograms = [...currentPrograms];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newPrograms.length) return;
    [newPrograms[index], newPrograms[newIndex]] = [newPrograms[newIndex], newPrograms[index]];
    setFormData(prev => ({
      ...prev,
      services: {
        ...prev.services,
        programs: newPrograms
      }
    }));
  };

  const updateProgramFeature = (programIndex, featureIndex, value) => {
    const currentPrograms = getPrograms();
    const newPrograms = [...currentPrograms];
    const newFeatures = [...newPrograms[programIndex].features];
    newFeatures[featureIndex] = value;
    newPrograms[programIndex] = { ...newPrograms[programIndex], features: newFeatures };
    setFormData(prev => ({
      ...prev,
      services: {
        ...prev.services,
        programs: newPrograms
      }
    }));
  };

  const addProgramFeature = (programIndex) => {
    const currentPrograms = getPrograms();
    const newPrograms = [...currentPrograms];
    newPrograms[programIndex] = {
      ...newPrograms[programIndex],
      features: [...newPrograms[programIndex].features, 'Yeni özellik']
    };
    setFormData(prev => ({
      ...prev,
      services: {
        ...prev.services,
        programs: newPrograms
      }
    }));
  };

  const deleteProgramFeature = (programIndex, featureIndex) => {
    const currentPrograms = getPrograms();
    const newPrograms = [...currentPrograms];
    newPrograms[programIndex] = {
      ...newPrograms[programIndex],
      features: newPrograms[programIndex].features.filter((_, i) => i !== featureIndex)
    };
    setFormData(prev => ({
      ...prev,
      services: {
        ...prev.services,
        programs: newPrograms
      }
    }));
  };

  const renderServicesForm = () => {
    const programs = getPrograms();
    
    return (
      <div className="content-form">
        <div className="form-group">
          <label className="form-label">Bölüm Başlığı</label>
          <input
            type="text"
            className="form-input"
            value={formData.services?.title || 'Eğitim Programlarımız'}
            onChange={(e) => handleChange('services', 'title', e.target.value)}
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Bölüm Açıklaması</label>
          <textarea
            className="form-textarea"
            rows={2}
            value={formData.services?.subtitle || 'Her seviyeye uygun, kapsamlı eğitim programlarımızla başarıya ulaşın'}
            onChange={(e) => handleChange('services', 'subtitle', e.target.value)}
          />
        </div>

        <div className="programs-header">
          <h3 className="form-subtitle">Programlar ({programs.length})</h3>
          <button type="button" className="btn btn-sm btn-primary" onClick={addProgram}>
            <FiPlus /> Yeni Program
          </button>
        </div>

        <div className="programs-list">
          {programs.map((program, index) => (
            <div key={program.id || index} className="program-item">
              <div className="program-item-header">
                <div className="program-item-title">
                  <div 
                    className="program-color-badge" 
                    style={{ background: program.color }}
                  />
                  <span>{program.title}</span>
                </div>
                <div className="program-item-actions">
                  <button 
                    type="button"
                    className="btn-icon"
                    onClick={() => moveProgram(index, 'up')}
                    disabled={index === 0}
                  >
                    <FiChevronUp />
                  </button>
                  <button 
                    type="button"
                    className="btn-icon"
                    onClick={() => moveProgram(index, 'down')}
                    disabled={index === programs.length - 1}
                  >
                    <FiChevronDown />
                  </button>
                  <button 
                    type="button"
                    className="btn-icon btn-danger"
                    onClick={() => deleteProgram(index)}
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
              
              <div className="program-item-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Program Adı</label>
                    <input
                      type="text"
                      className="form-input"
                      value={program.title}
                      onChange={(e) => updateProgram(index, 'title', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Alt Başlık</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Örn: 8. Sınıf Öğrencileri İçin"
                      value={program.subtitle || ''}
                      onChange={(e) => updateProgram(index, 'subtitle', e.target.value)}
                    />
                  </div>
                  <div className="form-group" style={{ maxWidth: '100px' }}>
                    <label className="form-label">Renk</label>
                    <input
                      type="color"
                      className="form-input color-picker"
                      value={program.color}
                      onChange={(e) => updateProgram(index, 'color', e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Açıklama</label>
                  <textarea
                    className="form-textarea"
                    rows={2}
                    value={program.description}
                    onChange={(e) => updateProgram(index, 'description', e.target.value)}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Süre</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Örn: 9 Ay"
                      value={program.duration || ''}
                      onChange={(e) => updateProgram(index, 'duration', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Sınıf Kontenjanı</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Örn: Max 15 Kişi"
                      value={program.classSize || ''}
                      onChange={(e) => updateProgram(index, 'classSize', e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Özellikler</label>
                  <div className="features-list">
                    {program.features.map((feature, fIndex) => (
                      <div key={fIndex} className="feature-item">
                        <input
                          type="text"
                          className="form-input"
                          value={feature}
                          onChange={(e) => updateProgramFeature(index, fIndex, e.target.value)}
                        />
                        <button 
                          type="button"
                          className="btn-icon btn-danger"
                          onClick={() => deleteProgramFeature(index, fIndex)}
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    ))}
                    <button 
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={() => addProgramFeature(index)}
                    >
                      <FiPlus /> Özellik Ekle
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ========== FEATURES FORM ==========
  const defaultFeatures = [
    { icon: 'award', title: 'Deneyimli Kadro', desc: 'Alanında uzman 50+ öğretmen' },
    { icon: 'book', title: 'Modern Müfredat', desc: 'Güncel eğitim programları' },
    { icon: 'users', title: 'Küçük Sınıflar', desc: 'Max 15 kişilik sınıflar' },
    { icon: 'target', title: 'Başarı Odaklı', desc: '%95 başarı oranı' }
  ];

  const iconOptions = [
    { value: 'award', label: '🏆 Ödül' },
    { value: 'book', label: '📚 Kitap' },
    { value: 'users', label: '👥 Kullanıcılar' },
    { value: 'target', label: '🎯 Hedef' },
    { value: 'star', label: '⭐ Yıldız' },
    { value: 'check', label: '✓ Onay' }
  ];

  const renderFeaturesForm = () => {
    const featureItems = formData.features?.items || defaultFeatures;
    
    return (
      <div className="content-form">
        <p className="form-info">Ana sayfadaki 4 özellik kartını düzenleyin</p>
        
        <div className="programs-list">
          {featureItems.map((feature, index) => (
            <div key={index} className="program-item">
              <div className="program-item-header">
                <span className="program-item-title">{feature.title}</span>
              </div>
              <div className="program-item-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Başlık</label>
                    <input
                      type="text"
                      className="form-input"
                      value={feature.title}
                      onChange={(e) => {
                        const newItems = [...featureItems];
                        newItems[index] = { ...newItems[index], title: e.target.value };
                        setFormData(prev => ({
                          ...prev,
                          features: { ...prev.features, items: newItems }
                        }));
                      }}
                    />
                  </div>
                  <div className="form-group" style={{ maxWidth: '150px' }}>
                    <label className="form-label">İkon</label>
                    <select
                      className="form-select"
                      value={feature.icon}
                      onChange={(e) => {
                        const newItems = [...featureItems];
                        newItems[index] = { ...newItems[index], icon: e.target.value };
                        setFormData(prev => ({
                          ...prev,
                          features: { ...prev.features, items: newItems }
                        }));
                      }}
                    >
                      {iconOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Açıklama</label>
                  <input
                    type="text"
                    className="form-input"
                    value={feature.desc}
                    onChange={(e) => {
                      const newItems = [...featureItems];
                      newItems[index] = { ...newItems[index], desc: e.target.value };
                      setFormData(prev => ({
                        ...prev,
                        features: { ...prev.features, items: newItems }
                      }));
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ========== TESTIMONIALS FORM ==========
  const defaultTestimonials = [
    { name: 'Ayşe Yılmaz', role: 'LGS Birincisi Velisi', text: 'Oğlum BMK sayesinde hayalindeki okula yerleşti.', rating: 5 },
    { name: 'Mehmet Kaya', role: 'Tıp Fakültesi Öğrencisi', text: 'YKS hazırlığımda BMK\'nın katkısı çok büyük.', rating: 5 },
    { name: 'Zeynep Demir', role: '9. Sınıf Öğrencisi', text: 'Küçük sınıflar sayesinde derslerim çok iyileşti.', rating: 5 }
  ];

  const renderTestimonialsForm = () => {
    const testimonialItems = formData.testimonials?.items || defaultTestimonials;
    
    return (
      <div className="content-form">
        <div className="form-group">
          <label className="form-label">Bölüm Başlığı</label>
          <input
            type="text"
            className="form-input"
            value={formData.testimonials?.title || 'Öğrenci ve Veli Yorumları'}
            onChange={(e) => handleChange('testimonials', 'title', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Alt Başlık</label>
          <input
            type="text"
            className="form-input"
            value={formData.testimonials?.subtitle || 'Başarıya ulaşan öğrencilerimizin deneyimleri'}
            onChange={(e) => handleChange('testimonials', 'subtitle', e.target.value)}
          />
        </div>
        
        <div className="programs-header">
          <h3 className="form-subtitle" style={{ margin: 0, padding: 0, border: 'none' }}>Yorumlar ({testimonialItems.length})</h3>
          <button type="button" className="btn btn-sm btn-primary" onClick={() => {
            setFormData(prev => ({
              ...prev,
              testimonials: {
                ...prev.testimonials,
                items: [...testimonialItems, { name: 'Yeni Kişi', role: 'Öğrenci', text: 'Yorum metni', rating: 5 }]
              }
            }));
          }}>
            <FiPlus /> Yorum Ekle
          </button>
        </div>

        <div className="programs-list">
          {testimonialItems.map((item, index) => (
            <div key={index} className="program-item">
              <div className="program-item-header">
                <span className="program-item-title">{item.name}</span>
                <button 
                  type="button"
                  className="btn-icon btn-danger"
                  onClick={() => {
                    if (!window.confirm('Bu yorumu silmek istediğinize emin misiniz?')) return;
                    setFormData(prev => ({
                      ...prev,
                      testimonials: {
                        ...prev.testimonials,
                        items: testimonialItems.filter((_, i) => i !== index)
                      }
                    }));
                  }}
                >
                  <FiTrash2 />
                </button>
              </div>
              <div className="program-item-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Ad Soyad</label>
                    <input
                      type="text"
                      className="form-input"
                      value={item.name}
                      onChange={(e) => {
                        const newItems = [...testimonialItems];
                        newItems[index] = { ...newItems[index], name: e.target.value };
                        setFormData(prev => ({
                          ...prev,
                          testimonials: { ...prev.testimonials, items: newItems }
                        }));
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Ünvan/Rol</label>
                    <input
                      type="text"
                      className="form-input"
                      value={item.role}
                      onChange={(e) => {
                        const newItems = [...testimonialItems];
                        newItems[index] = { ...newItems[index], role: e.target.value };
                        setFormData(prev => ({
                          ...prev,
                          testimonials: { ...prev.testimonials, items: newItems }
                        }));
                      }}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Yorum</label>
                  <textarea
                    className="form-textarea"
                    rows={2}
                    value={item.text}
                    onChange={(e) => {
                      const newItems = [...testimonialItems];
                      newItems[index] = { ...newItems[index], text: e.target.value };
                      setFormData(prev => ({
                        ...prev,
                        testimonials: { ...prev.testimonials, items: newItems }
                      }));
                    }}
                  />
                </div>
                <div className="form-group" style={{ maxWidth: '150px' }}>
                  <label className="form-label">Puan (1-5)</label>
                  <select
                    className="form-select"
                    value={item.rating}
                    onChange={(e) => {
                      const newItems = [...testimonialItems];
                      newItems[index] = { ...newItems[index], rating: parseInt(e.target.value) };
                      setFormData(prev => ({
                        ...prev,
                        testimonials: { ...prev.testimonials, items: newItems }
                      }));
                    }}
                  >
                    <option value={5}>⭐⭐⭐⭐⭐ (5)</option>
                    <option value={4}>⭐⭐⭐⭐ (4)</option>
                    <option value={3}>⭐⭐⭐ (3)</option>
                    <option value={2}>⭐⭐ (2)</option>
                    <option value={1}>⭐ (1)</option>
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ========== CTA FORM ==========
  const renderCtaForm = () => (
    <div className="content-form">
      <h3 className="form-subtitle">Ana Sayfa CTA</h3>
      <p className="form-info">Ana sayfanın alt kısmındaki aksiyon çağrısı bölümü</p>
      
      <div className="form-group">
        <label className="form-label">Başlık</label>
        <input
          type="text"
          className="form-input"
          value={formData.cta?.title || 'Geleceğiniz İçin İlk Adımı Atın'}
          onChange={(e) => handleChange('cta', 'title', e.target.value)}
        />
      </div>
      <div className="form-group">
        <label className="form-label">Açıklama</label>
        <textarea
          className="form-textarea"
          rows={2}
          value={formData.cta?.description || 'Bursluluk sınavımıza başvurun, %100\'e varan burs fırsatlarından yararlanın!'}
          onChange={(e) => handleChange('cta', 'description', e.target.value)}
        />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Birincil Buton Metni</label>
          <input
            type="text"
            className="form-input"
            value={formData.cta?.primaryButton || 'Hemen Başvur'}
            onChange={(e) => handleChange('cta', 'primaryButton', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">İkincil Buton Metni</label>
          <input
            type="text"
            className="form-input"
            value={formData.cta?.secondaryButton || 'Bize Ulaşın'}
            onChange={(e) => handleChange('cta', 'secondaryButton', e.target.value)}
          />
        </div>
      </div>

      <h3 className="form-subtitle" style={{ marginTop: '2rem' }}>Programlar Sayfası CTA</h3>
      <p className="form-info">Programlar sayfasındaki aksiyon çağrısı bölümü</p>
      
      <div className="form-group">
        <label className="form-label">Başlık</label>
        <input
          type="text"
          className="form-input"
          value={formData.cta?.programsTitle || 'Sizin İçin En Uygun Programı Belirleyelim'}
          onChange={(e) => handleChange('cta', 'programsTitle', e.target.value)}
        />
      </div>
      <div className="form-group">
        <label className="form-label">Açıklama</label>
        <textarea
          className="form-textarea"
          rows={2}
          value={formData.cta?.programsDescription || 'Ücretsiz danışmanlık hizmetimizden yararlanın, çocuğunuzun ihtiyaçlarına en uygun programı birlikte seçelim.'}
          onChange={(e) => handleChange('cta', 'programsDescription', e.target.value)}
        />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Birincil Buton Metni</label>
          <input
            type="text"
            className="form-input"
            value={formData.cta?.programsPrimaryButton || 'Ücretsiz Danışmanlık'}
            onChange={(e) => handleChange('cta', 'programsPrimaryButton', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">İkincil Buton Metni</label>
          <input
            type="text"
            className="form-input"
            value={formData.cta?.programsSecondaryButton || 'Bizi Arayın'}
            onChange={(e) => handleChange('cta', 'programsSecondaryButton', e.target.value)}
          />
        </div>
      </div>
    </div>
  );

  // ========== SCHOLARSHIP FORM ==========
  const defaultBenefits = [
    { icon: 'percent', title: '%100\'e Varan Burs', desc: 'Başarınıza göre tam burs imkanı' },
    { icon: 'award', title: 'Ücretsiz Deneme', desc: 'Sınavda başarılı olun, ücretsiz deneyin' },
    { icon: 'gift', title: 'Özel Hediyeler', desc: 'Başarılı öğrencilere sürpriz hediyeler' }
  ];

  const addBenefit = () => {
    const current = formData.scholarship?.benefits || defaultBenefits;
    setFormData(prev => ({
      ...prev,
      scholarship: { ...prev.scholarship, benefits: [...current, { icon: 'award', title: 'Yeni Başlık', desc: 'Açıklama' }] }
    }));
  };

  const updateBenefit = (index, field, value) => {
    const current = formData.scholarship?.benefits || defaultBenefits;
    const newItems = [...current];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData(prev => ({
      ...prev,
      scholarship: { ...prev.scholarship, benefits: newItems }
    }));
  };

  const deleteBenefit = (index) => {
    if (!window.confirm('Bu kartı silmek istediğinize emin misiniz?')) return;
    const current = formData.scholarship?.benefits || defaultBenefits;
    setFormData(prev => ({
      ...prev,
      scholarship: { ...prev.scholarship, benefits: current.filter((_, i) => i !== index) }
    }));
  };

  const renderScholarshipForm = () => {
    const benefitsList = formData.scholarship?.benefits || defaultBenefits;
    
    return (
      <div className="content-form">
        <p className="form-info">Bursluluk sınavı sayfasındaki avantaj kartları</p>
        
        <div className="programs-header">
          <h3 className="form-subtitle" style={{ margin: 0, padding: 0, border: 'none' }}>Avantaj Kartları</h3>
          <button type="button" className="btn btn-sm btn-primary" onClick={addBenefit}>
            <FiPlus /> Ekle
          </button>
        </div>

        <div className="programs-list">
          {benefitsList.map((benefit, index) => (
            <div key={index} className="program-item">
              <div className="program-item-header">
                <span className="program-item-title">{benefit.title}</span>
                <button
                  type="button"
                  className="btn-icon btn-danger"
                  onClick={() => deleteBenefit(index)}
                >
                  <FiTrash2 />
                </button>
              </div>
              <div className="program-item-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">İkon</label>
                    <select
                      className="form-select"
                      value={benefit.icon}
                      onChange={(e) => updateBenefit(index, 'icon', e.target.value)}
                    >
                      <option value="percent">Yüzde (%)</option>
                      <option value="award">Ödül</option>
                      <option value="gift">Hediye</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Başlık</label>
                    <input
                      type="text"
                      className="form-input"
                      value={benefit.title}
                      onChange={(e) => updateBenefit(index, 'title', e.target.value)}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Açıklama</label>
                  <input
                    type="text"
                    className="form-input"
                    value={benefit.desc}
                    onChange={(e) => updateBenefit(index, 'desc', e.target.value)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ========== GALLERY FORM ==========
  const defaultCategories = [];

  const addCategory = () => {
    const current = formData.gallery?.categories || defaultCategories;
    setFormData(prev => ({
      ...prev,
      gallery: { ...prev.gallery, categories: [...current, { id: `cat-${Date.now()}`, label: 'Yeni Kategori' }] }
    }));
  };

  const updateCategory = (index, field, value) => {
    const current = formData.gallery?.categories || defaultCategories;
    const newItems = [...current];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData(prev => ({
      ...prev,
      gallery: { ...prev.gallery, categories: newItems }
    }));
  };

  const deleteCategory = (index) => {
    if (!window.confirm('Bu kategoriyi silmek istediğinize emin misiniz?')) return;
    const current = formData.gallery?.categories || defaultCategories;
    setFormData(prev => ({
      ...prev,
      gallery: { ...prev.gallery, categories: current.filter((_, i) => i !== index) }
    }));
  };

  const renderGalleryForm = () => {
    const categoriesList = formData.gallery?.categories || defaultCategories;
    
    return (
      <div className="content-form">
        <p className="form-info">Galeri sayfasında görünecek kategori filtreleri. Kategori ekleyerek resimleri gruplandırabilirsiniz.</p>
        
        <div className="programs-header">
          <h3 className="form-subtitle" style={{ margin: 0, padding: 0, border: 'none' }}>Kategoriler</h3>
          <button type="button" className="btn btn-sm btn-primary" onClick={addCategory}>
            <FiPlus /> Ekle
          </button>
        </div>

        {categoriesList.length === 0 ? (
          <div className="empty-state" style={{ textAlign: 'center', padding: '2rem', color: 'var(--gray-500)' }}>
            <FiImage style={{ fontSize: '2rem', marginBottom: '0.5rem' }} />
            <p>Henüz kategori eklenmemiş</p>
          </div>
        ) : (
          <div className="programs-list">
            {categoriesList.map((category, index) => (
              <div key={index} className="program-item">
                <div className="program-item-header">
                  <span className="program-item-title">{category.label}</span>
                  <button
                    type="button"
                    className="btn-icon btn-danger"
                    onClick={() => deleteCategory(index)}
                  >
                    <FiTrash2 />
                  </button>
                </div>
                <div className="program-item-body">
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">ID (Teknik)</label>
                      <input
                        type="text"
                        className="form-input"
                        value={category.id}
                        onChange={(e) => updateCategory(index, 'id', e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                        placeholder="ornek-kategori"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Görünen İsim</label>
                      <input
                        type="text"
                        className="form-input"
                        value={category.label}
                        onChange={(e) => updateCategory(index, 'label', e.target.value)}
                        placeholder="Örnek Kategori"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderForm = () => {
    switch (activeSection) {
      case 'hero': return renderHeroForm();
      case 'features': return renderFeaturesForm();
      case 'about': return renderAboutForm();
      case 'services': return renderServicesForm();
      case 'testimonials': return renderTestimonialsForm();
      case 'stats': return renderStatsForm();
      case 'cta': return renderCtaForm();
      case 'scholarship': return renderScholarshipForm();
      case 'gallery': return renderGalleryForm();
      case 'contact': return renderContactForm();
      case 'general': return renderGeneralForm();
      default: return null;
    }
  };

  return (
    <div className="admin-content-page">
      {/* Tabs */}
      <div className="content-tabs">
        {sections.map((section) => (
          <button
            key={section.id}
            className={`content-tab ${activeSection === section.id ? 'active' : ''}`}
            onClick={() => setActiveSection(section.id)}
          >
            <section.icon />
            {section.label}
          </button>
        ))}
      </div>

      {/* Content Area */}
      <motion.div 
        className="content-area"
        key={activeSection}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="content-header">
          <h2>{sections.find(s => s.id === activeSection)?.label}</h2>
          <div className="content-actions">
            <button className="btn btn-secondary" onClick={handleReset}>
              <FiRefreshCw />
              Geri Al
            </button>
            <button 
              className="btn btn-primary" 
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className="spinner" style={{ width: 16, height: 16 }}></span>
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <FiSave />
                  Kaydet
                </>
              )}
            </button>
          </div>
        </div>

        {renderForm()}
      </motion.div>

      {/* Image Picker Modal */}
      <ImagePickerModal
        isOpen={imagePickerOpen}
        onClose={() => {
          setImagePickerOpen(false);
          setImagePickerTarget(null);
        }}
        onSelect={handleImageSelect}
        currentImage={
          imagePickerTarget 
            ? formData[imagePickerTarget.section]?.[imagePickerTarget.field] 
            : null
        }
      />
    </div>
  );
};

export default AdminContent;

